import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { LayoutObjectType, RouteStepResponse } from '@yard/app/api'
import { useYardStores } from '@yard/AppProvider'
import { routeService } from '@yard/services/route.service'
import React, { useCallback } from 'react'
import { RoutingSlipMiniMap } from './RoutingSlipMiniMap'
import { buildStopList } from './resolve-stops'

/**
 * Returns a `renderRouteMiniMap(visitId)` function suitable for passing to
 * GateInFormDialog / GateClerkHomePage. The hook also loads required stores —
 * call `loadStores()` once (e.g. in a useEffect) before the first render.
 *
 * The route is fetched from `/yard/api/Routes/TruckVisit/{visitId}`. For
 * single-gate terminals the entry and exit gate IDs are the same.
 */
export const useRoutingSlipMiniMap = () => {
  const { yardBlockStore, layoutObjectStore, roadNetworkStore } = useYardStores()
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const loadStores = useCallback(() => {
    layoutObjectStore.loadLayoutObjects()
    roadNetworkStore.loadAll()
    yardBlockStore.loadYardBlocks()
  }, [layoutObjectStore, roadNetworkStore, yardBlockStore])

  const renderRouteMiniMap = async (
    visitId: number | undefined,
  ): Promise<React.ReactElement | null> => {
    if (!visitId) return null

    const gate = layoutObjectStore.items.find(lo => lo.type === LayoutObjectType.Gate) ?? null

    if (!gate) return null

    let route
    try {
      route = await routeService.getTruckVisitRoute(visitId, gate.id, gate.id)
    } catch (err) {
      // Since the minimap is rendered on the PDF, it should not throw error otherwise it will break the Routing slip
      console.error(`[RoutingSlipMiniMap] failed to fetch route for visitId=${visitId}`, err)
      return null
    }

    const stops = buildStopList(
      route.stops,
      route.unreachableBlockIds,
      yardBlockStore.yardBlocks,
      yardBlockStore.items,
      layoutObjectStore.layoutObjects,
    )

    if (stops.length === 0) return null

    // Split the flat step list into entry (gate → last stop) and exit
    // (last stop → gate). We walk steps and consume stops in order — the exit
    // only starts once every stop has been visited. This handles patterns like
    // A → B → A, where the last stop shares a nodeId with the first.
    // Unreachable blocks carry an empty nodeId and are skipped.
    const stopNodeIds = stops.filter(s => s.nodeId !== '').map(s => s.nodeId)
    const entrySteps: RouteStepResponse[] = []
    const exitSteps: RouteStepResponse[] = []
    if (stopNodeIds.length > 0) {
      let nextStopIdx = 0
      for (const step of route.steps) {
        if (nextStopIdx >= stopNodeIds.length) {
          exitSteps.push(step)
        } else {
          entrySteps.push(step)
          if (step.toNodeId === stopNodeIds[nextStopIdx]) nextStopIdx++
        }
      }
    } else {
      entrySteps.push(...route.steps)
    }

    // Only handover areas that are actually stops get drawn on the map.
    const stopHandoverIds = new Set(
      stops.filter(s => s.kind === 'handover').map(s => s.id),
    )
    const stopHandoverAreas = layoutObjectStore.items.filter(lo => stopHandoverIds.has(lo.id))

    return (
      <RoutingSlipMiniMap
        entrySteps={entrySteps}
        exitSteps={exitSteps}
        allYardBlocks={yardBlockStore.items}
        stopHandoverAreas={stopHandoverAreas}
        roadSegments={roadNetworkStore.segments}
        stops={stops}
        roadNodes={roadNetworkStore.nodes}
        gate={gate}
        primaryColor={theme.palette.primary.darker}
        secondaryColor={theme.palette.warning.main}
        labels={{
          startEnd: t('startEnd', 'start / end'),
          youAreHere: t('youAreHere', 'you are here'),
          entryRoute: t('entryRoute', 'Entry route'),
          exitRoute: t('exitRoute', 'Exit route'),
          otherRoads: t('otherRoads', 'Other roads'),
          block: t('block', 'Block'),
        }}
      />
    )
  }

  return { loadStores, renderRouteMiniMap }
}
